import { Editor } from '@ckeditor/ckeditor5-core';
import { AxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorInfo, OriSearchConditions } from '../../../../../api/model';
import {
  useGetSectionsOrisearchMasters,
  usePostSectionsOridownload,
  usePostSectionsOrisearch,
} from '../../../../../api/sections/sections';
import { useDefaultOptions, useOptionsForBinary } from '../../../../../hooks/api';
import { executeDownload } from '../../../../../utils/download';
import * as sessionStorageUtils from '../../../../../utils/sessionStorage';
import ErrorComponent from '../../../../atoms/ErrorComponent';
import NoDataComponent from '../../../../atoms/NoDataComponent';
import WaitComponent from '../../../../atoms/WaitComponent';
import BlobErrorModal from '../../../../modules/BlobErrorModal';
import OkModal from '../../../../modules/OkModal';
import SpinnerModal from '../../../../modules/SpinnerModal';
import OriList from './OriList';
import OriSearchCondition from './OriSearchCondition';

interface Props {
  editor: Editor;
  productionId: number | undefined;
}

function OriPage({ editor, productionId }: Props) {
  // 言語情報
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // マスタ、組織情報の取得
  const options = useDefaultOptions(language);

  const {
    data: mastersData,
    isLoading: mastersIsLoading,
    isError: mastersIsError,
    error: mastersError,
  } = useGetSectionsOrisearchMasters(options);

  // 初期検索条件（セッションストレージに保存されている）
  const [defaultConditions] = useState<OriSearchConditions>(
    //   sessionStorageUtils.get<OriSearchConditions>('Sections.OriSearchConditions', {})
    {}
  );

  // 検索条件
  const refConditions = useRef(sessionStorageUtils.get<OriSearchConditions>('Sections.OriSearchConditions', {}));
  // 子コンポーネント（OriList）からチェックの値を取得する
  const [selectSectionIdsRef, setSelectSectionsRef] = useState<number[]>([]);
  // スピナーモーダルを表示する
  const [displaySpinnerModal, setDisplaySpinnerModal] = useState(false);
  // レコード無し表示
  const [displayDownLoadNoRec, setdisplayDownLoadNoRec] = useState(false);

  // 検索 API 情報
  const {
    data: Oridata,
    isLoading: oriIsLoading,
    isError: oriIsError,
    error: oriError,
    mutateAsync: searchAsync,
  } = usePostSectionsOrisearch<AxiosError<ErrorInfo>, unknown>(options);

  // 条件を保存し、検索する
  const setConditionsAndSearch = useCallback(
    (next: OriSearchConditions) => {
      const postPostSectionsOrisearch = async () => {
        // API アクセスが成功した場合
        const onSuccess = () => {
          // sessionStorageUtils.set('Sections.OriSearchConditions', next);
          refConditions.current = next;
        };

        // API アクセスの実行
        await searchAsync({ data: next }, { onSuccess });
      };
      void postPostSectionsOrisearch();
    },
    [searchAsync]
  );

  // 一覧出力
  const optionsForBinary = useOptionsForBinary(language);
  const {
    mutateAsync: _downloadAsync,
    isError: downloadIsError,
    error: downloadError,
  } = usePostSectionsOridownload<AxiosError<Blob>>(optionsForBinary);
  const [displayDownloadError, setDisplayDownloadError] = useState(false);

  useEffect(() => {
    setDisplayDownloadError(downloadIsError);
  }, [downloadIsError]);

  // ダウンロード処理
  const downloadAsync = useCallback(
    async (ids: number[] | []) => {
      if (ids.length) {
        setDisplaySpinnerModal(true);
        const postdata = { section_ids: ids };
        await _downloadAsync(
          { data: postdata },
          {
            onSuccess: (response) => {
              executeDownload(response);
              setDisplaySpinnerModal(false);
            },
            onError: () => {
              setDisplaySpinnerModal(false);
            },
          }
        );
      } else {
        setdisplayDownLoadNoRec(true);
      }
    },
    [_downloadAsync]
  );

  // 待ち状態
  if (mastersIsLoading) {
    return <WaitComponent />;
  }

  // エラー
  if (mastersIsError) {
    return (
      <div id="main">
        {mastersIsError && <ErrorComponent error={mastersError} />}
        {oriIsError && <ErrorComponent error={oriError} />}
      </div>
    );
  }

  // データが取得できない場合
  if (
    mastersData.data.production_divisions == null ||
    mastersData.data.destinations == null ||
    mastersData.data.model_users == null ||
    mastersData.data.companies == null ||
    mastersData.data.development_scales == null ||
    mastersData.data.languages == null ||
    mastersData.data.chapter_numbers == null ||
    mastersData.data.levels == null ||
    mastersData.data.comment_divisions == null ||
    mastersData.data.is_browsing == null ||
    mastersData.data.model_names == null
  ) {
    return <NoDataComponent />;
  }

  return (
    <div>
      {/* 検索条件 */}
      <OriSearchCondition
        defaultConditions={defaultConditions}
        conditionsRef={refConditions}
        productionDivisions={mastersData.data.production_divisions}
        modelNames={mastersData.data.model_names}
        destinations={mastersData.data.destinations}
        modelUsers={mastersData.data.model_users}
        companies={mastersData.data.companies}
        developmentScales={mastersData.data.development_scales}
        languages={mastersData.data.languages}
        chapterNumbers={mastersData.data.chapter_numbers}
        levels={mastersData.data.levels}
        commentDivisions={mastersData.data.comment_divisions}
        isBrowsing={mastersData.data.is_browsing}
        setConditionsAndSearch={setConditionsAndSearch}
      />

      <div className="outputfr mt10 mb10">
        <span
          onClick={() => {
            // オプションが選択したらダウンロードを実行
            void downloadAsync(selectSectionIdsRef);
          }}
        >
          <i className="fa-solid fa-download" />
          {t('出力')}
        </span>
      </div>
      {/* 検索条件 */}
      <OriList
        editor={editor}
        productionId={productionId}
        data={Oridata}
        isLoading={oriIsLoading}
        isError={oriIsError}
        error={oriError}
        setSelectSectionsRef={setSelectSectionsRef}
      />
      {/* スピナーモーダル */}
      {displaySpinnerModal && <SpinnerModal />}
      {/* 注意メッセージ */}
      {displayDownLoadNoRec && (
        <OkModal onClickOk={() => setdisplayDownLoadNoRec(false)}>
          <div>{t('出力対象を選択してください。')}</div>
        </OkModal>
      )}
      {/* ダウンロードエラー */}
      {displayDownloadError && (
        <BlobErrorModal error={downloadError} onClickOk={() => setDisplayDownloadError(false)} />
      )}
    </div>
  );
}

export default OriPage;
